import useEmblaCarousel from 'embla-carousel-react'
import { FC, cloneElement, useMemo, useState } from 'react'
import classNames from 'classnames'

import { Carousel, useMediaQuery, Lightbox } from '@lounge-fe/ui-kit'

import { MediaImageProps, MediaItemElement, MediaProps } from './types'
import { ModelSelector } from './ModelSelector'
import { AddToWishlist } from '@/features/product/components/AddToWishlist'

const MODEL_REGEX = /^.+\s\|\s(.+,\s.+)$/

export const getModelByAlt = ({ alt }: { alt?: string }) =>
  alt?.match(MODEL_REGEX)?.[1] ?? undefined

const Media: FC<MediaProps> = ({ className, children, productId }) => {
  const [emblaRef, embla] = useEmblaCarousel({ align: 'start', loop: true })
  const isDesktop = useMediaQuery(({ screens }) => `(min-width: ${screens.md})`)

  const [models] = useState(
    new Set(
      children
        .map(({ props }) => getModelByAlt(props))
        .filter(Boolean) as string[],
    ),
  )

  const [selectedModel, setSelectedModel] = useState<string>(
    models.size ? models.values().next().value : undefined,
  )

  const media = useMemo(
    () =>
      models.size && selectedModel
        ? children.filter(
            (child) => getModelByAlt(child.props) === selectedModel,
          )
        : children,

    [children, models, selectedModel],
  )

  const lightboxImages = useMemo(() => {
    const filtered = media.filter(
      (child) => child.type.mediaType === 'image',
    ) as MediaItemElement<MediaImageProps>[]
    return filtered.map(
      (child) =>
        ({
          ...child.props,
          data: child.props.lightboxImageData ?? child.props.data,
          thumbnailImageData: child.props.thumbnailImageData,
        }) as MediaImageProps,
    )
  }, [media])

  if (!media.length) {
    return null
  }

  if (isDesktop) {
    return (
      <Lightbox media={lightboxImages}>
        <div className={classNames('grid grid-cols-2 gap-1', className)}>
          {media.map((child, idx) => (
            <div
              className={classNames(
                'w-full relative',
                idx % 3 === 0
                  ? 'col-span-2 aspect-[2/3] 2xl:aspect-[unset] 2xl:h-[calc(100vh_-_var(--header-height-with-announcement))]'
                  : 'col-span-2 2xl:col-span-1 [&_img]:h-auto [&_img]:aspect-[2/3]',
              )}
              key={idx}
            >
              {child.type.mediaType === 'image' ? (
                <>
                  <Lightbox.Trigger index={idx}>
                    <button className="overflow-hidden bg-surface-primary-default w-full h-full block relative">
                      {child}
                    </button>
                  </Lightbox.Trigger>
                  {idx === 0 && models.size > 1 && (
                    <ModelSelector
                      models={models}
                      selectedModel={selectedModel}
                      onChange={setSelectedModel}
                    />
                  )}
                </>
              ) : (
                child
              )}
            </div>
          ))}
        </div>
      </Lightbox>
    )
  }

  return (
    <>
      <Carousel emblaRef={emblaRef} embla={embla}>
        <Carousel.Viewport className="relative">
          <Carousel.Container className="auto-cols-[100%]">
            {media.map((child, idx) => (
              <Carousel.Slide
                key={idx}
                index={idx}
                className="aspect-[2/3] sm:aspect-[1/1]"
              >
                {child}
              </Carousel.Slide>
            ))}
          </Carousel.Container>
          {models.size > 0 && (
            <ModelSelector
              models={models}
              selectedModel={selectedModel}
              onChange={setSelectedModel}
            />
          )}
        </Carousel.Viewport>
        <Carousel.Progress />
      </Carousel>
    </>
  )
}

export default Media
